import { WithholdingTransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import { TaxQuarter } from "@wingspanhq/bookkeeping/dist/lib/interfaces/taxEstimate";
import {
  toWSDateString,
  WSActions,
  WSAlert,
  WSCard,
  WSContainer,
  WSElement,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  getCurrentTaxPaymentQuarter,
  Quarter
} from "@wingspanhq/utils/dist/date/taxDeadlines";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMutationCreatePendingWithholding } from "../../../../query/taxes/mutations";
import { useQueryWithholdingBalance } from "../../../../query/taxes/queries";
import { WSQueries } from "../../../../query/WSQuery";
import {
  useQueryActivities,
  useQueryMemberProfile
} from "../../../../query/users/queries";
import { CurrencyUSD } from "../../../../services/bookkeeping";
import { FromTo } from "../../../../shared/components/FromTo";
import { selectorTaxWithholdingAccount } from "../../../../shared/selectors/selectorTaxWithholdingAccount";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { LabelMethod } from "../../../Settings/components/LabelMethod";
import {
  PATH_TAXES,
  routeHelpersTaxesSchedulePaymentConfirmation
} from "../../paths";
import { useModalAddSSN } from "./ModalAddSSN";
import { useModalConsentEFTPS } from "./ModalConsentEFTPS";
import { Layout } from "../../../../shared/components/Layout";
import { formatInternalDeadline } from "../../utils";

const quarterInfo = getCurrentTaxPaymentQuarter();

const quarterToTaxQuarter: Record<Quarter, TaxQuarter> = {
  [Quarter.Q1]: TaxQuarter.q1,
  [Quarter.Q2]: TaxQuarter.q2,
  [Quarter.Q3]: TaxQuarter.q3,
  [Quarter.Q4]: TaxQuarter.q4
};

type SchedulePaymentErrorProps = {
  error?: WSServiceError | null;
  internalDeadline: Date;
  quarter: Quarter;
  year: number;
};

export const SchedulePaymentError: React.FC<SchedulePaymentErrorProps> = ({
  error,
  internalDeadline,
  quarter,
  year
}) => {
  const duplicatePaymentError = `A pending tax payment already exists for ${quarter} ${year}`;

  if (error?.response?.data?.error !== duplicatePaymentError) {
    return <WSErrorMessage contextKey="PendingWithholding" error={error} />;
  }

  return (
    <WSAlert
      theme="error"
      icon="info-circle"
      title={`Unable to schedule the payment. A pending tax payment already exists for ${quarter} ${year}.`}
      data-testid="ws-form-api-error"
    >
      <WSList gap="S">
        <WSText.ParagraphSm weight="medium">
          Need to make changes?
        </WSText.ParagraphSm>
        <WSText.ParagraphSm>
          You can cancel and reschedule anytime before this quarter&apos;s
          processing deadline: {formatInternalDeadline(internalDeadline)}. After
          this date your payment will be locked for processing and cannot be
          cancelled.
        </WSText.ParagraphSm>
      </WSList>
    </WSAlert>
  );
};

export const FormSchedulePayment: React.FC = () => {
  const history = useHistory();
  const userId = useUserId();

  const goToConfirmation =
    routeHelpersTaxesSchedulePaymentConfirmation.useGoTo();

  const modalAddSSN = useModalAddSSN();
  const modalConsentEFTPS = useModalConsentEFTPS();

  const queryMemberProfile = useQueryMemberProfile(userId);
  const queryActivity = useQueryActivities(userId);
  const queryWithholdingBalance = useQueryWithholdingBalance();
  const queryInternalAccounts = useQueryInternalAccounts();

  const [createPendingWithholding, createPendingWithholdingMeta] =
    useMutationCreatePendingWithholding();

  const [amount, setAmount] = useState<number | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!amount) return;

    if (!queryMemberProfile.data?.profile.ssnProvided) {
      const ssnResult = await modalAddSSN.open();
      if (!ssnResult) return;
    }

    if (!queryActivity.data?.events.eftpsEnrolledAt) {
      const eftpsResult = await modalConsentEFTPS.open();
      if (!eftpsResult) return;
    }

    await createPendingWithholding(
      {
        amount,
        type: WithholdingTransactionType.TaxPayment,
        currency: CurrencyUSD,
        quarter: quarterToTaxQuarter[quarterInfo.quarter],
        year: quarterInfo.year
      },
      {
        onSuccess: result => {
          goToConfirmation({
            pendingWithholdingId: result.pendingWithholdingId
          });
        }
      }
    );
  }, [
    createPendingWithholding,
    goToConfirmation,
    modalAddSSN,
    modalConsentEFTPS,
    queryActivity.data,
    queryMemberProfile.data,
    amount
  ]);

  return (
    <Layout
      onClose={() => history.push(PATH_TAXES)}
      title={`Schedule payment to IRS - ${quarterInfo.quarter} ${quarterInfo.year}`}
      onBack={() => history.goBack()}
      wide
    >
      <WSQueries
        queries={{
          queryWithholdingBalance,
          queryMemberProfile,
          queryActivity,
          queryInternalAccounts
        }}
      >
        {({
          queryWithholdingBalanceData,
          queryMemberProfileData,
          queryInternalAccountsData
        }) => {
          const availableBalance = queryWithholdingBalanceData.available || 0;
          const taxWithholdingAccount = queryInternalAccountsData.find(
            selectorTaxWithholdingAccount
          );
          const ssnLast4 = queryMemberProfileData.profile.ssnLastFourProvided
            ? "last 4"
            : "";

          const dueDateFormatted = toWSDateString(
            quarterInfo.irsDeadline,
            "monthDayYear"
          );

          const isAmountValid = !!(amount && amount <= availableBalance);

          return (
            <WSContainer verticalPadding>
              <WSList gap="XL" mt="M">
                <WSElement>
                  <WSSectionToolbar title="We will submit your payment to the IRS on your behalf." />
                  <WSText.ParagraphSm mt="S" color="gray500">
                    This payment will be scheduled for {quarterInfo.quarter}{" "}
                    {quarterInfo.year}.
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm mt="S" color="gray500">
                    To ensure timely processing, please submit your payment by{" "}
                    <b>
                      {formatInternalDeadline(quarterInfo.internalDeadline)}
                    </b>
                    .
                  </WSText.ParagraphSm>
                </WSElement>

                <WSCard header="Amount">
                  <WSList gap="XL">
                    <WSInputNumber
                      size="L"
                      value={amount}
                      onChange={setAmount}
                      currency={"USD"}
                      maxFractionDigits={2}
                      max={availableBalance}
                    />

                    <WSInfoBox>
                      <WSText.ParagraphSm color="gray500">
                        Available balance:{" "}
                        {formatMoney(availableBalance, "USD")}
                      </WSText.ParagraphSm>
                    </WSInfoBox>

                    <FromTo
                      fromLabel="From"
                      fromContent={
                        taxWithholdingAccount ? (
                          <LabelMethod
                            type="InternalAccount"
                            id={taxWithholdingAccount.internalAccountId}
                          />
                        ) : (
                          <WSText.ParagraphSm>
                            Tax Withholdings
                          </WSText.ParagraphSm>
                        )
                      }
                      toLabel="To"
                      toContent={
                        <WSText.ParagraphSm>
                          Scheduled payment to IRS - {quarterInfo.quarter}{" "}
                          {quarterInfo.year} by {dueDateFormatted}
                        </WSText.ParagraphSm>
                      }
                    />

                    <WSAlert
                      theme="info"
                      icon="info-circle"
                      title="Understanding your tax payment"
                    >
                      Your verified SSN (
                      {ssnLast4 ? `---${ssnLast4}` : "on file"}) from your
                      account details will be used for tax payments.
                    </WSAlert>

                    <SchedulePaymentError
                      error={createPendingWithholdingMeta.error}
                      internalDeadline={quarterInfo.internalDeadline}
                      quarter={quarterInfo.quarter}
                      year={quarterInfo.year}
                    />

                    <WSActions
                      alignment="fill"
                      buttons={[
                        {
                          kind: "Secondary",
                          label: "Cancel",
                          type: "button",
                          onClick: () => history.goBack()
                        },
                        {
                          label: "Request scheduled payment",
                          disabled: !isAmountValid,
                          loading: createPendingWithholdingMeta.isLoading,
                          type: "submit",
                          onAsyncClick: handleSubmit
                        }
                      ]}
                    />
                  </WSList>
                </WSCard>
              </WSList>
            </WSContainer>
          );
        }}
      </WSQueries>
    </Layout>
  );
};
